import React, { useState, useRef, useCallback } from 'react';
import { Upload, X, AlertCircle, Loader2, Image as ImageIcon } from 'lucide-react';
import { extractApiError } from '../utils/errorUtils';

interface VendorImageUploaderProps {
  images: string[];
  maxImages?: number;
  maxFileSize?: number; // in bytes
  onUpload: (file: File) => Promise<string>;
  onRemove: (imageUrl: string) => Promise<void>;
  disabled?: boolean;
}

const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const DEFAULT_MAX_SIZE = 5 * 1024 * 1024; // 5MB
const DEFAULT_MAX_IMAGES = 10;

export default function VendorImageUploader({
  images,
  maxImages = DEFAULT_MAX_IMAGES,
  maxFileSize = DEFAULT_MAX_SIZE,
  onUpload,
  onRemove,
  disabled = false,
}: VendorImageUploaderProps) {
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const canAddMore = images.length < maxImages;

  const validateFile = useCallback((file: File): string | null => {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      return 'Please upload a JPEG, PNG, or WebP image';
    }
    if (file.size > maxFileSize) {
      const maxMB = Math.round(maxFileSize / (1024 * 1024));
      return `File size must be less than ${maxMB}MB`;
    }
    return null;
  }, [maxFileSize]);

  const handleUpload = useCallback(async (file: File) => {
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    // Check directly against images.length for accurate validation
    if (images.length >= maxImages) {
      setError(`Maximum ${maxImages} images allowed`);
      return;
    }

    try {
      setError(null);
      setUploading(true);
      await onUpload(file);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      setError(apiError.message);
    } finally {
      setUploading(false);
    }
  }, [images.length, maxImages, validateFile, onUpload]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleUpload(file);
    }
    // Reset input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [handleUpload]);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);

    if (disabled || uploading) return;

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleUpload(file);
    }
  }, [disabled, uploading, handleUpload]);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!disabled && !uploading) {
      setDragOver(true);
    }
  }, [disabled, uploading]);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const handleRemove = useCallback(async (imageUrl: string) => {
    try {
      setError(null);
      setRemoving(imageUrl);
      await onRemove(imageUrl);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      setError(apiError.message);
    } finally {
      setRemoving(null);
    }
  }, [onRemove]);

  const handleClick = () => {
    if (!disabled && !uploading && canAddMore) {
      fileInputRef.current?.click();
    }
  };

  return (
    <div className="space-y-4">
      {/* Error Message */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>{error}</span>
          <button
            onClick={() => setError(null)}
            className="ml-auto text-red-500 hover:text-red-700"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Image Grid */}
      {images.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {images.map((imageUrl, index) => (
            <div
              key={imageUrl}
              className="relative aspect-square rounded-lg overflow-hidden bg-gray-100 group"
            >
              <img
                src={imageUrl}
                alt={`Portfolio image ${index + 1}`}
                className="w-full h-full object-cover"
                loading="lazy"
              />
              {/* Remove button */}
              {!disabled && (
                <button
                  onClick={() => handleRemove(imageUrl)}
                  disabled={removing === imageUrl}
                  className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600 disabled:opacity-50"
                  title="Remove image"
                >
                  {removing === imageUrl ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <X className="w-4 h-4" />
                  )}
                </button>
              )}
              {/* Index badge */}
              <div className="absolute bottom-2 left-2 px-2 py-0.5 bg-black/50 text-white text-xs rounded">
                {index + 1}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload Zone */}
      {canAddMore && !disabled && (
        <div
          onClick={handleClick}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          className={`
            relative border-2 border-dashed rounded-lg p-8 text-center cursor-pointer
            transition-colors
            ${dragOver ? 'border-primary bg-teal-50' : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'}
            ${uploading ? 'pointer-events-none opacity-50' : ''}
          `}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_TYPES.join(',')}
            onChange={handleFileSelect}
            className="hidden"
          />

          {uploading ? (
            <div className="flex flex-col items-center gap-2">
              <Loader2 className="w-10 h-10 text-secondary animate-spin" />
              <p className="text-gray-600">Uploading...</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              {dragOver ? (
                <>
                  <ImageIcon className="w-10 h-10 text-teal-500" />
                  <p className="text-secondary font-medium">Drop image here</p>
                </>
              ) : (
                <>
                  <Upload className="w-10 h-10 text-gray-400" />
                  <p className="text-gray-600">
                    <span className="font-medium text-secondary">Click to upload</span> or drag and drop
                  </p>
                  <p className="text-sm text-gray-500">
                    JPEG, PNG, or WebP (max {Math.round(maxFileSize / (1024 * 1024))}MB)
                  </p>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Image Count */}
      <p className="text-sm text-gray-500 text-center">
        {images.length} of {maxImages} images uploaded
      </p>
    </div>
  );
}
